import { useMemo } from "react";
import { Link } from "react-router-dom";
import type { PublicLessonMaterial } from "@vedaunion/shared";

/**
 * Lesson materials — the learner's companion rail/sheet: grouped by kind, the
 * most common actions (open the PDF, play the recording, watch the talk) one tap
 * away.
 *
 * NOT to be confused with the Library's material browser
 * (`components/document/MaterialCollection.tsx`), which reads the normalised
 * `Material` records in `components/document/materials.ts`. This one renders
 * `PublicLessonMaterial` inside a lesson. If the two ever need to look alike,
 * move this onto that model rather than copying its markup across.
 *
 * `variant`:
 *   - "rail"  → compact vertical list (the sticky desktop right rail / mobile
 *               bottom sheet).
 *   - "full"  → larger spacing for a standalone section.
 *
 * Group order: PDFs → audio (segmented by groupKey) → playlists → videos →
 * documents → external links → other files.
 */
export function MaterialList({
  materials,
  variant = "rail",
}: {
  materials: PublicLessonMaterial[];
  variant?: "rail" | "full";
}) {
  const groups = useMemo(() => groupMaterials(materials), [materials]);

  if (materials.length === 0) {
    return (
      <p className="font-grotesk text-[0.85rem] text-[var(--color-ink-mute)]">
        No materials attached to this lesson.
      </p>
    );
  }

  const gap = variant === "full" ? "gap-7" : "gap-6";

  return (
    <div className={`flex flex-col ${gap}`}>
      {groups.pdfs.length > 0 && (
        <Section eyebrow="Texts" title="Texts & PDFs">
          <ul className="flex flex-col">
            {groups.pdfs.map((m) => (
              <li key={m.id} className="border-t border-[var(--color-rule)] first:border-t-0">
                <a
                  href={m.fileUrl ?? "#"}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex items-baseline justify-between gap-4 py-3 min-h-[44px]"
                >
                  <span className="min-w-0">
                    <span className="block font-grotesk font-semibold text-[0.98rem] leading-[1.25] group-hover:text-[var(--color-violet)] transition-colors">
                      {m.label}
                    </span>
                    {(m.script || m.description) && (
                      <span className="block font-grotesk text-[0.62rem] tracking-[0.16em] uppercase text-[var(--color-ink-mute)] mt-1">
                        {m.script ?? "PDF"}
                        {m.description ? ` · ${m.description}` : ""}
                      </span>
                    )}
                  </span>
                  <span className="flex-none self-center font-grotesk text-[0.66rem] tracking-[0.16em] uppercase text-[var(--color-ink-mute)] group-hover:text-[var(--color-violet)] transition-colors">
                    PDF&nbsp;↓
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {groups.audioGroups.map((g) => (
        <Section
          key={g.key ?? "audio"}
          eyebrow={g.key ? `Audio · ${g.key}` : "Audio"}
          title={g.key ? `${capitalize(g.key)} recordings` : "Recordings"}
        >
          <ul className="flex flex-col">
            {g.items.map((m) => (
              <li
                key={m.id}
                className="border-t border-[var(--color-rule)] first:border-t-0 py-2.5"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-grotesk font-semibold text-[0.95rem] leading-[1.25]">
                      {m.label}
                    </p>
                    {m.description && (
                      <p className="font-grotesk text-[0.82rem] text-[var(--color-ink-soft)] mt-0.5">
                        {m.description}
                      </p>
                    )}
                  </div>
                  {m.durationSec !== null && (
                    <span className="font-grotesk text-[0.66rem] tracking-[0.14em] uppercase text-[var(--color-ink-mute)] flex-shrink-0">
                      {formatDuration(m.durationSec)}
                    </span>
                  )}
                </div>
                {m.fileUrl && (
                  <audio
                    controls
                    preload="none"
                    src={m.fileUrl}
                    className="w-full mt-2"
                    style={{ height: 34 }}
                  />
                )}
              </li>
            ))}
          </ul>
        </Section>
      ))}

      {groups.playlists.length > 0 && (
        <Section eyebrow="Playlists" title="Watch the series">
          <ul className="flex flex-col gap-3">
            {groups.playlists.map((m) => (
              <li key={m.id}>
                <a
                  href={`https://www.youtube.com/playlist?list=${m.youtubePlaylistId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex gap-3 items-start py-1"
                >
                  {m.youtubePlaylist?.thumbnailUrl && (
                    <img
                      src={m.youtubePlaylist.thumbnailUrl}
                      alt=""
                      loading="lazy"
                      className="flex-none w-24 aspect-video object-cover border border-[var(--color-rule)] rounded-[var(--radius-card)]"
                    />
                  )}
                  <div className="min-w-0">
                    <p className="font-grotesk text-[0.6rem] tracking-[0.18em] uppercase text-[var(--color-violet)] mb-1">
                      Playlist
                      {m.youtubePlaylist?.itemCount ? (
                        <>
                          <span className="mx-1.5">·</span>
                          {m.youtubePlaylist.itemCount} videos
                        </>
                      ) : null}
                    </p>
                    <p className="font-grotesk font-semibold text-[0.95rem] leading-[1.25] group-hover:text-[var(--color-violet)] transition-colors">
                      {m.youtubePlaylist?.title ?? m.label}
                    </p>
                  </div>
                </a>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {groups.videos.length > 0 && (
        <Section eyebrow="Videos" title="Recorded talks">
          <ul className="flex flex-col gap-3">
            {groups.videos.map((m) => (
              <li key={m.id}>
                <a
                  href={`https://www.youtube.com/watch?v=${m.youtubeVideoId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex gap-3 items-start py-1"
                >
                  {m.youtubeVideo?.thumbnailUrl && (
                    <img
                      src={m.youtubeVideo.thumbnailUrl}
                      alt=""
                      loading="lazy"
                      className="flex-none w-24 aspect-video object-cover border border-[var(--color-rule)] rounded-[var(--radius-card)]"
                    />
                  )}
                  <div className="min-w-0">
                    <p className="font-grotesk font-semibold text-[0.95rem] leading-[1.25] group-hover:text-[var(--color-violet)] transition-colors">
                      {m.youtubeVideo?.title ?? m.label}
                    </p>
                    {m.youtubeVideo?.durationSec ? (
                      <p className="font-grotesk text-[0.62rem] tracking-[0.14em] uppercase text-[var(--color-ink-mute)] mt-1">
                        {formatDuration(m.youtubeVideo.durationSec)}
                      </p>
                    ) : null}
                  </div>
                </a>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {groups.documents.length > 0 && (
        <Section eyebrow="From the library" title="Related documents">
          <ul className="flex flex-col">
            {groups.documents.map((m) => (
              <li key={m.id} className="border-t border-[var(--color-rule)] first:border-t-0">
                <Link
                  to={m.document ? `/library/${m.document.slug}` : "#"}
                  className="group flex items-center gap-3 py-3 min-h-[44px]"
                >
                  <div className="media-frame flex-none w-12 h-12">
                    {m.document?.coverImage ? (
                      <img
                        src={m.document.coverImage}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full grid place-items-center font-deva text-lg text-[var(--color-ink-light)]">
                        ॐ
                      </div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="font-grotesk font-semibold text-[0.95rem] leading-[1.25] line-clamp-2 group-hover:text-[var(--color-violet)] transition-colors">
                      {m.document?.title ?? m.label}
                    </p>
                    {m.document?.subtitle && (
                      <p className="font-grotesk text-[0.82rem] text-[var(--color-ink-mute)] line-clamp-1 mt-0.5">
                        {m.document.subtitle}
                      </p>
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {groups.links.length > 0 && (
        <Section eyebrow="Links" title="Elsewhere on the web">
          <ul className="flex flex-col">
            {groups.links.map((m) => (
              <li
                key={m.id}
                className="border-t border-[var(--color-rule)] first:border-t-0 py-2.5"
              >
                <a
                  href={m.externalUrl ?? "#"}
                  target="_blank"
                  rel="noreferrer"
                  className="group block min-h-[44px]"
                >
                  <span className="font-grotesk font-semibold text-[0.95rem] leading-[1.25] group-hover:text-[var(--color-violet)] transition-colors">
                    {m.label} <span className="opacity-60 ml-1">↗</span>
                  </span>
                  {m.description && (
                    <span className="block font-grotesk text-[0.82rem] text-[var(--color-ink-soft)] mt-0.5">
                      {m.description}
                    </span>
                  )}
                  {m.externalUrl && (
                    <span className="block font-grotesk text-[0.65rem] text-[var(--color-ink-mute)] mt-0.5 truncate">
                      {tidyHostname(m.externalUrl)}
                    </span>
                  )}
                </a>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {groups.files.length > 0 && (
        <Section eyebrow="Files" title="Downloads">
          <ul className="flex flex-col">
            {groups.files.map((m) => (
              <li key={m.id} className="border-t border-[var(--color-rule)] first:border-t-0">
                <a
                  href={m.fileUrl ?? "#"}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex items-baseline justify-between gap-4 py-3 min-h-[44px]"
                >
                  <span className="font-grotesk font-semibold text-[0.95rem] leading-[1.25] group-hover:text-[var(--color-violet)] transition-colors">
                    {m.label}
                  </span>
                  <span className="flex-none self-center font-grotesk text-[0.66rem] tracking-[0.16em] uppercase text-[var(--color-ink-mute)] group-hover:text-[var(--color-violet)] transition-colors">
                    ↓
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------

function Section({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <header className="mb-2.5">
        <p className="font-grotesk text-[0.62rem] tracking-[0.16em] uppercase font-semibold text-[var(--color-violet)]">
          {eyebrow}
        </p>
        <h3 className="font-grotesk font-semibold text-[0.95rem] leading-[1.25] mt-0.5 text-[var(--color-ink)]">
          {title}
        </h3>
      </header>
      {children}
    </section>
  );
}

function groupMaterials(materials: PublicLessonMaterial[]) {
  const ordered = [...materials].sort((a, b) => a.sortOrder - b.sortOrder);
  const pdfs = ordered.filter((m) => m.kind === "pdf");
  const audio = ordered.filter((m) => m.kind === "audio");
  const videos = ordered.filter((m) => m.kind === "youtube_video");
  const playlists = ordered.filter((m) => m.kind === "youtube_playlist");
  const documents = ordered.filter((m) => m.kind === "document");
  const links = ordered.filter((m) => m.kind === "external_link");
  const files = ordered.filter((m) => m.kind === "video");

  const groupOrder: Array<string | null> = [];
  const groupMap = new Map<string | null, PublicLessonMaterial[]>();
  for (const m of audio) {
    const k = m.groupKey ?? null;
    if (!groupMap.has(k)) {
      groupMap.set(k, []);
      groupOrder.push(k);
    }
    groupMap.get(k)!.push(m);
  }
  const audioGroups = groupOrder.map((k) => ({ key: k, items: groupMap.get(k)! }));

  return { pdfs, audioGroups, videos, playlists, documents, links, files };
}

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return `${h}h ${String(mm).padStart(2, "0")}m`;
  }
  return `${m}:${String(s).padStart(2, "0")}`;
}

function tidyHostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
